/**
 * GlyphRenderingEngine - Main glyph rendering loop for EdgeDocs
 *
 * Extracted from EdgeDocsCanvas.V3.tsx (lines 1678-1720) as part of Phase 3 Step 4.
 *
 * This module handles the core text rendering pipeline:
 * - Snapshot data integrity validation
 * - Duplicate draw detection (debug tripwire)
 * - Main rendering loop through lines and glyphs
 * - Integration with EngineTextRenderer for actual drawing
 *
 * The rendering engine is designed to be stateless - all state is passed in
 * via the RenderGlyphsParams interface, making it easier to test and debug.
 */

import { EngineTextRenderer } from '../../../../../rendering/engine/EngineTextRenderer';
import { CanvasLogger } from '../../../../../../../utils/loggers/CanvasLogger';

/**
 * Line data from WASM snapshot
 */
export interface SnapshotLine {
  start_grapheme_index: number;
  end_grapheme_index: number;
  baseline_y_fp?: number;
  runs?: Array<{
    glyphs?: Array<{
      id?: number;
      [key: string]: any;
    }>;
    [key: string]: any;
  }>;
  [key: string]: any;
}

/**
 * Snapshot data from WASM typesetter
 */
export interface RenderSnapshot {
  lines: SnapshotLine[];
  caret_map?: any[];
  source?: string;
  [key: string]: any;
}

/**
 * Parameters for rendering glyphs
 */
export interface RenderGlyphsParams {
  /** Canvas 2D rendering context */
  ctx: CanvasRenderingContext2D;
  /** Snapshot data from WASM typesetter */
  snapshot: RenderSnapshot;
  /** Transform scale factor (World Space → Canvas Space) */
  transformScale: number;
  /** Current page index (for debugging) */
  pageIndex: number;
  /** Unique cycle ID for duplicate detection */
  cycleId: number;
  /** Debug logging function */
  debugLog: (...args: any[]) => void;
  /** Enable detailed diagnostics */
  enableDiagnostics?: boolean;
}

/**
 * Result of rendering glyphs
 */
export interface RenderGlyphsResult {
  /** Number of lines successfully rendered */
  renderedLines: number;
  /** Total number of lines in snapshot */
  totalLines: number;
  /** Any duplicate ranges detected (debug) */
  duplicateRanges: string[];
  /** Line errors encountered during rendering */
  lineErrors: Array<{ lineIndex: number; error: Error }>;
}

/**
 * Line range info for duplicate detection
 */
interface LineRangeInfo {
  idx: number;
  start: number;
  end: number;
  key: string;
}

/**
 * Get or initialize the global draw count map for duplicate detection
 */
function getDrawCountMap(): Record<string, number> {
  if (!(window as any).__ED_DRAW_COUNT__) {
    (window as any).__ED_DRAW_COUNT__ = {};
  }
  return (window as any).__ED_DRAW_COUNT__;
}

/**
 * Clear previous cycle's data from draw count map
 */
function clearPreviousCycleData(drawMap: Record<string, number>, currentCycleId: number): void {
  for (const k of Object.keys(drawMap)) {
    if (!k.startsWith(`${currentCycleId}|`)) {
      delete drawMap[k];
    }
  }
}

/**
 * Validate snapshot data integrity and detect duplicate line ranges
 *
 * @param lines - Snapshot lines to validate
 * @param cycleId - Current render cycle ID
 * @param pageIndex - Current page index
 * @returns Object with line ranges and any duplicates found
 */
export function validateSnapshotIntegrity(
  lines: SnapshotLine[],
  cycleId: number,
  pageIndex: number
): { lineRanges: LineRangeInfo[]; duplicateRanges: string[] } {
  const lineRanges = lines.map((l, i) => ({
    idx: i,
    start: l.start_grapheme_index,
    end: l.end_grapheme_index,
    key: `${l.start_grapheme_index}-${l.end_grapheme_index}`
  }));

  const rangeKeys = lineRanges.map(r => r.key);
  const duplicateRanges = rangeKeys.filter((k, i) => rangeKeys.indexOf(k) !== i);

  if (duplicateRanges.length > 0) {
    CanvasLogger.lines.yCollision(pageIndex, duplicateRanges.length, [], {
      cycleId,
      duplicates: duplicateRanges,
      allRanges: lineRanges,
      bugType: 'DUPLICATE_LINE_RANGES'
    });
  }

  // Log line positions with y_position for coalesce-to-line-0 bug detection
  const linePositions = lines.map(line => ({
    start: line.start_grapheme_index,
    end: line.end_grapheme_index,
    y: (line.baseline_y_fp || 0) / 64  // Convert fixed-point to pixels
  }));
  
  CanvasLogger.lines.positions(pageIndex, linePositions, {
    cycleId,
    ranges: lineRanges.map(r => r.key)
  });

  return { lineRanges, duplicateRanges };
}

/**
 * Check for duplicate draw of same line in same cycle
 *
 * @param drawMap - The global draw count map
 * @param cycleId - Current render cycle ID
 * @param pageIndex - Current page index
 * @param lineIndex - Current line index
 * @param line - The line being drawn
 * @returns true if this is a duplicate draw
 */
function checkDuplicateDraw(
  drawMap: Record<string, number>,
  cycleId: number,
  pageIndex: number,
  lineIndex: number,
  line: SnapshotLine
): boolean {
  const drawKey = `${cycleId}|p=${pageIndex}|line=${lineIndex}|g=${line.start_grapheme_index}-${line.end_grapheme_index}|y=${Math.round((line.baseline_y_fp || 0) / 64)}`;
  drawMap[drawKey] = (drawMap[drawKey] || 0) + 1;

  if (drawMap[drawKey] === 2) {
    CanvasLogger.lines.yCollision(pageIndex, 1, [Math.round((line.baseline_y_fp || 0) / 64)], {
      drawKey,
      bugType: 'DUPLICATE_DRAW_SAME_CYCLE',
      cycleId,
      lineIndex
    });
    return true;
  }

  return false;
}

/**
 * Render glyphs from a WASM snapshot to the canvas
 *
 * This is the main rendering function that iterates through all lines in the
 * snapshot and renders each glyph using the EngineTextRenderer. It includes
 * comprehensive diagnostic and duplicate detection capabilities for debugging.
 *
 * @param params - Rendering parameters
 * @returns Render result with statistics and any errors
 *
 * @example
 * ```typescript
 * const result = renderGlyphs({
 *   ctx: canvas.getContext('2d')!,
 *   snapshot: wasmSnapshot,
 *   transformScale: transform.scale,
 *   pageIndex: 0,
 *   cycleId: 'render-001',
 *   debugLog: console.log,
 *   enableDiagnostics: true
 * });
 *
 * console.log(`Rendered ${result.renderedLines}/${result.totalLines} lines`);
 * ```
 */
export function renderGlyphs(params: RenderGlyphsParams): RenderGlyphsResult {
  const {
    ctx,
    snapshot,
    transformScale,
    pageIndex,
    cycleId,
    debugLog,
    enableDiagnostics = true
  } = params;

  const snapshotLines = snapshot.lines || [];
  const lineErrors: Array<{ lineIndex: number; error: Error }> = [];

  // Validate snapshot integrity (debug diagnostics)
  let duplicateRanges: string[] = [];
  if (enableDiagnostics) {
    const validation = validateSnapshotIntegrity(snapshotLines, cycleId, pageIndex);
    duplicateRanges = validation.duplicateRanges;
  }

  // Initialize draw tripwire for duplicate detection
  const drawMap = getDrawCountMap();
  clearPreviousCycleData(drawMap, cycleId);

  // Create text renderer
  const textRenderer = new EngineTextRenderer(ctx);

  // Main rendering loop
  let renderedLines = 0;
  for (let lineIndex = 0; lineIndex < snapshotLines.length; lineIndex++) {
    const line = snapshotLines[lineIndex];

    try {
      // Check for duplicate draw (debug tripwire)
      if (enableDiagnostics) {
        checkDuplicateDraw(drawMap, cycleId, pageIndex, lineIndex, line);
      }

      // Render the line using EngineTextRenderer
      textRenderer.drawLineFromSnapshot(line, transformScale);
      renderedLines++;
    } catch (lineError) {
      console.error(`🎨 [GlyphRenderingEngine] Failed to render line ${lineIndex}:`, lineError);
      lineErrors.push({ lineIndex, error: lineError as Error });
    }
  }

  debugLog(`🎨 [GlyphRenderingEngine] Successfully rendered ${renderedLines}/${snapshotLines.length} lines`);

  return {
    renderedLines,
    totalLines: snapshotLines.length,
    duplicateRanges,
    lineErrors
  };
}

/**
 * Log DOM diagnostics for the canvas container
 *
 * Useful for debugging issues with multiple canvases or input elements
 * that might interfere with rendering.
 *
 * @param canvas - The canvas element
 * @param pageIndex - Current page index
 */
export function logDomDiagnostics(canvas: HTMLCanvasElement, pageIndex: number): void {
  const pageContainer = canvas.parentElement;
  if (pageContainer) {
    const canvases = pageContainer.querySelectorAll('canvas');
    const inputs = pageContainer.querySelectorAll('input');
    const editables = pageContainer.querySelectorAll('[contenteditable="true"]');
    const canvasSizes = Array.from(canvases).map(c => `${c.width}×${c.height}`);
    CanvasLogger.domDiag.pageContainer(pageIndex, canvases.length, canvasSizes, inputs.length, editables.length);
  }
}
